/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
import EntryPointSelect from '../../Fields/EntryPointSelect';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';

// misc imports
import useTheme from '../../../../utils/hooks/useTheme';
import { FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH } from '../../../../constants/deploymentFormPaths';

const FACEBOOK_CALL_TO_ACTIONS = {
  POSTBACK: 'Postback',
  WEB_URL: 'Web URL'
};

const BotDeploymentsFacebookStrategyGeneralForm = ({
  form,
  hasEditAccess,
  isLoadingMessengerProfile,
  fetchMessengerProfileError
}) => {
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const pageAccessToken = Form.useWatch([...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageAccessToken'], form);

  useEffect(() => {
    setTimeout(() => {
      form.validateFields();
    }, 100);
  }, []);

  return (
    <>
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Row>
            <Col flex="auto">
              <Typography.Text>
                {t('Application Settings')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the application settings')}
              </Typography.Paragraph>
            </Col>
          </Row>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            label={t('Page Access Token')}
            name={[...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageAccessToken']}
            rules={[{
              required: true,
              message: t('Please input Page Access Token')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Enter page access token')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageAccessToken'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Page ID')}
            name={[...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageId']}
            rules={[{
              required: true,
              message: t('Please input Page ID')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Enter Page ID')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageId'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('App Secret')}
            name={[...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'appSecret']}
            tooltip={t('Facebook App Secret used to verify webhook signatures (X-Hub-Signature-256). If not set, webhook payloads are accepted without verification.')}
          >
            {
              hasEditAccess
                ? <Input.Password placeholder={t('Enter Facebook App Secret')} />
                : <Typography.Text>{'••••••••'}</Typography.Text>
            }
          </Form.Item>
        </Col>
      </Row>

      <Fragment>
        <Divider />

        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Row>
              <Col flex="auto">
                <Typography.Text>
                  {t('Messenger Profile Settings')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Configure messenger profile settings')}
                </Typography.Paragraph>
              </Col>
            </Row>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              label={t('Get started button trigger flow')}
              name={'get_started'}
              dependencies={['call_to_actions']}
              validateTrigger={['onChange', 'onBlur']}
              rules={[
                {
                  validator: async (_rule, value) => {
                    // Get started must be set if call_to_actions is not empty
                    const callToActions = form.getFieldValue('call_to_actions');
                    if (callToActions && callToActions.length > 0 && !value) {
                      return Promise.reject(new Error(t('Required field if persistent menu actions are set')));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              {
                hasEditAccess
                  ? <EntryPointSelect
                    allowClear
                    placeholder={t('Select trigger flow')}
                    disabled={!pageAccessToken || isLoadingMessengerProfile || fetchMessengerProfileError}
                  />
                  : <Typography.Text>{form.getFieldValue(['get_started'])}</Typography.Text>
              }
            </Form.Item>

            <Form.Item
              label={t('Greeting')}
              name={'greeting'}
            >
              {
                hasEditAccess
                  ? <Input placeholder={t('Enter your text')} disabled={!pageAccessToken || isLoadingMessengerProfile || fetchMessengerProfileError}/>
                  : <Typography.Text>{form.getFieldValue(['greeting'])}</Typography.Text>
              }
            </Form.Item>
            <Form.Item label={t('Persistent Menu Actions')}>
              <Form.List name={'call_to_actions'}>
                {(fields, { add, remove }) => {
                  return (
                    <Fragment>
                      {fields.map((field, index) => (
                        <Card
                          size="small"
                          className="bg-layout-gray shadow-none position-relative mb-4"
                          key={field.key}
                        >
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item
                                {...field}
                                label={t('Type')}
                                name={[field.name, 'type']}
                                rules={[{ required: true, message: t('Missing type') }]}
                              >
                                <Select
                                  placeholder={t('Select type')}
                                  onChange={() => {
                                    // Force the re-render of the fields
                                    form.setFieldsValue({
                                      call_to_actions: form.getFieldValue('call_to_actions')
                                    });
                                  }}
                                  disabled={!pageAccessToken || !hasEditAccess || isLoadingMessengerProfile || fetchMessengerProfileError}
                                  getPopupContainer={triggerNode => triggerNode.parentElement}
                                >
                                  {
                                    Object.keys(FACEBOOK_CALL_TO_ACTIONS).map((key) => (
                                      <Select.Option key={key} value={key}>
                                        {FACEBOOK_CALL_TO_ACTIONS[key]}
                                      </Select.Option>
                                    ))
                                  }
                                </Select>
                              </Form.Item>
                            </Col>
                            {form.getFieldValue(['call_to_actions', field.name, 'type']) && (
                              <Fragment>
                                <Col span={24}>
                                  <Form.Item
                                    shouldUpdate={(prevValues, curValues) =>
                                      prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                                    }
                                  >
                                    {
                                      () => (
                                        <Form.Item
                                          {...field}
                                          label={t('Action Title')}
                                          name={[field.name, 'title']}
                                          rules={[{ required: true, message: t('Missing action title') }]}
                                          className="mb-0"
                                        >
                                          {
                                            hasEditAccess
                                              ? <Input allowClear placeholder={t('Enter the action title')} disabled={!pageAccessToken || isLoadingMessengerProfile || fetchMessengerProfileError} showCount maxLength={30}/>
                                              : <Typography.Text>{form.getFieldValue(['call_to_actions', field.key, 'title'])}</Typography.Text>
                                          }
                                        </Form.Item>
                                      )
                                    }
                                  </Form.Item>
                                </Col>

                                <Col span={24}>
                                  <Form.Item
                                    shouldUpdate={(prevValues, curValues) =>
                                      prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                                    }
                                  >
                                    {() => {
                                      const type = form.getFieldValue(['call_to_actions', field.name, 'type']);
                                      const isPostback = type === Object.keys(FACEBOOK_CALL_TO_ACTIONS)[0];
                                      return (
                                        <Form.Item
                                          {...field}
                                          label={isPostback ? t('Flow Triggered') : t('URL')}
                                          name={[field.name, isPostback ? 'data' : 'url']}
                                          rules={[{
                                            required: true,
                                            message: t(isPostback ? t('Missing flow') : t('Enter a valid URL')),
                                            type: !isPostback ? 'url' : null
                                          }]}
                                          className="mb-0"
                                        >
                                          {hasEditAccess ? (
                                            isPostback ? (
                                              <EntryPointSelect
                                                allowClear
                                                placeholder={t('Select a trigger flow')}
                                                disabled={!pageAccessToken}
                                              />
                                            ) : (
                                              <Input placeholder={t('Enter a URL')} disabled={!pageAccessToken || isLoadingMessengerProfile || fetchMessengerProfileError} />
                                            )
                                          ) : (
                                            <Typography.Text>
                                              {form.getFieldValue(['call_to_actions', field.key, isPostback ? 'data' : 'url'])}
                                            </Typography.Text>
                                          )}
                                        </Form.Item>
                                      );
                                    }}
                                  </Form.Item>
                                </Col>
                              </Fragment>)}
                          </Row>

                          {hasEditAccess ? (
                            <Button
                              type="text"
                              onClick={() => remove(field.name)}
                              shape="circle"
                              icon={<DeleteIcon />}
                              disabled={!pageAccessToken || isLoadingMessengerProfile || fetchMessengerProfileError}
                              className="float-right bordered border-radius-base"
                            />
                          ) : null}
                        </Card>
                      ))}

                      <Form.Item shouldUpdate>
                        {hasEditAccess ? (
                          <Button
                            disabled={!pageAccessToken || fields.length >= 3 || isLoadingMessengerProfile || fetchMessengerProfileError}
                            type="dashed"
                            onClick={() => add()}
                            icon={<PlusIcon />}
                          >
                            {t('Add action')}
                          </Button>
                        ) : null}
                      </Form.Item>
                    </Fragment>
                  );
                }}
              </Form.List>
            </Form.Item>
          </Col>
        </Row>
      </Fragment>
    </>
  );
};

BotDeploymentsFacebookStrategyGeneralForm.propTypes = {
  form: PropTypes.object,
  hasEditAccess: PropTypes.bool,
  isLoadingMessengerProfile: PropTypes.bool,
  fetchMessengerProfileError: PropTypes.bool
};

export default BotDeploymentsFacebookStrategyGeneralForm;
